import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Button,
  makeStyles,
} from "@material-ui/core";
import { Select } from "@ofqual/portal-components/components/Select";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { useDispatch } from "react-redux";
import { DateTimePicker } from "@ofqual/portal-components/components/DateTimePicker";
import { Error } from "../../../common/Error";

import { SocEvent } from "../../../../domain/SocEvent";
import { SocItem } from "../../../../domain/SocItem";
import { fieldLabelsOfqual } from "../../../sections/fieldLabels";
import { eventTypesService } from "../../../../services";
import { addEvent } from "../../../../redux/actions";

const useStyles = makeStyles((theme) => ({
  changeDetails: {
    padding: 15,
  },
  actions: {
    padding: 15,
  },
  disabledLink: {
    color: "inherit",
    textDecoration: "none",
  },
}));

export const AddEventDialog: React.FC<{
  onClose: () => void;
  resource: SocItem;
}> = ({ onClose, resource }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isSaveInProgress, setIsSaveInProgress] = useState<boolean>(false);

  const { handleSubmit, control, errors } = useForm<SocEvent>({
    defaultValues: { meetingDate: null, eventType: null },
  });

  const [eventTypesLookups] = useLookup(eventTypesService);

  const onSubmit = (newEvent: SocEvent) => {
    setIsSaveInProgress(true);

    dispatch(addEvent(resource?.id!, newEvent));
    onClose();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="add-event-dialog"
    >
      <DialogTitle data-test="add-event-title">Add an event</DialogTitle>
      <Divider component="div" />
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.changeDetails}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="meetingDate"
                  as={
                    <DateTimePicker
                      dataTest="add-event-date"
                      label={fieldLabelsOfqual.meetingDate}
                      required
                    />
                  }
                />
                {errors.meetingDate?.type === "required" && (
                  <Error data-test="meetingDate-validation">
                    This field is required
                  </Error>
                )}
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="eventType"
                  rules={{ required: true }}
                  as={
                    <Select
                      selectVariant="single"
                      label={fieldLabelsOfqual.eventType}
                      datatest="eventType"
                      required
                    >
                      {eventTypesLookups}
                    </Select>
                  }
                />
                {errors.eventType?.type === "required" && (
                  <Error data-test="eventType-validation">
                    This field is required
                  </Error>
                )}
              </Grid>
            </Grid>
          </div>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          data-test="cancel-event-button"
          color="primary"
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit(onSubmit)}
          data-test="add-event-modal-button"
          className={isSaveInProgress ? classes.disabledLink : undefined}
          disabled={isSaveInProgress}
          color="primary"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
